import React, { useEffect, useState } from 'react';
import { listRoles, assignRole, unassignRole } from './rolesApi';
import { toastSuccess, toastError, errorMessage } from '../../../lib/toast';
import './roles.css';

/**
 * Drawer section (rendered inside the admin-only user drawer) that lists
 * every role with a checkbox reflecting whether the given user holds it.
 * Toggling a checkbox assigns/unassigns the role immediately.
 *
 * Props:
 *   userId            id of the user being edited
 *   currentRoleKeys   string[] — role keys currently held by the user
 *   onChanged         () => void  called after each successful toggle so the
 *                      parent can refresh the user's role list
 */
export default function UserRolesSection({ userId, currentRoleKeys, onChanged }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pendingKey, setPendingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    listRoles()
      .then((r) => { if (!cancelled) setRoles(r); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const currentKeys = currentRoleKeys || [];
  // System roles (admin, dept_manager) are derived/managed via their own
  // dedicated mechanisms (Make/Remove-admin action, department_managers
  // junction) — never through this generic toggle. See roleDerivation.js.
  const assignableRoles = roles.filter((role) => !role.is_system);

  const handleToggle = async (role, checked) => {
    setPendingKey(role.key);
    try {
      if (checked) {
        await assignRole(userId, role.key);
        toastSuccess(`${role.label} assigned.`);
      } else {
        await unassignRole(userId, role.key);
        toastSuccess(`${role.label} unassigned.`);
      }
      onChanged?.();
    } catch (err) {
      toastError(errorMessage(err, `Could not update ${role.label}.`));
    } finally {
      setPendingKey(null);
    }
  };

  if (loading) return <div className="rbac-skeleton" aria-busy="true">Loading roles…</div>;
  if (error) return <div className="rbac-error">Could not load roles.</div>;

  return (
    <section className="ut-roles" aria-label="Manage roles">
      <ul className="ut-roles-list">
        {assignableRoles.map((role) => {
          const checked = currentKeys.includes(role.key);
          return (
            <li key={role.key} className="ut-roles-item">
              <label>
                <input
                  type="checkbox"
                  aria-label={role.label}
                  checked={checked}
                  disabled={pendingKey === role.key}
                  onChange={(e) => handleToggle(role, e.target.checked)}
                />
                <span title={role.description || undefined}>{role.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
